import React, { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  twitterCard?: 'summary' | 'summary_large_image';
  noIndex?: boolean;
  structuredData?: Record<string, any>;
}

/**
 * SEO component for managing document head metadata
 * 
 * This component updates the document head with SEO-relevant tags
 * including title, meta descriptions, Open Graph tags, and more.
 * 
 * @param {SEOProps} props - SEO configuration props
 * @returns {null} - This component doesn't render anything visible
 */
const SEO: React.FC<SEOProps> = ({
  title = 'Raidu | Enterprise AI Governance Platform',
  description = 'Raidu is an enterprise AI governance platform that helps organizations adopt AI safely and securely with data masking, prompt improvement, private RAG, and multi-LLM support.',
  canonical,
  ogImage = '/images/raidu-og-image.png',
  ogType = 'website',
  twitterCard = 'summary_large_image',
  noIndex = false,
  structuredData,
}) => {
  useEffect(() => {
    // Update document title
    document.title = title;
    
    // Get all existing meta tags
    const existingMetaTags = document.querySelectorAll('meta');
    const metaTagsToRemove: HTMLMetaElement[] = [];
    
    // Identify meta tags that need to be replaced
    existingMetaTags.forEach(tag => {
      const name = tag.getAttribute('name');
      const property = tag.getAttribute('property');
      
      if (
        name === 'description' || 
        name === 'robots' ||
        property?.startsWith('og:') || 
        property?.startsWith('twitter:')
      ) {
        metaTagsToRemove.push(tag as HTMLMetaElement);
      }
    });
    
    // Remove old meta tags
    metaTagsToRemove.forEach(tag => tag.remove());
    
    // Create and append new meta tags
    const metaTags = [
      { name: 'description', content: description },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:type', content: ogType },
      { property: 'og:image', content: new URL(ogImage, window.location.origin).href },
      { property: 'og:url', content: canonical || window.location.href },
      { property: 'twitter:card', content: twitterCard },
      { property: 'twitter:title', content: title },
      { property: 'twitter:description', content: description },
      { property: 'twitter:image', content: new URL(ogImage, window.location.origin).href },
    ];
    
    // Add robots meta tag if noIndex is true
    if (noIndex) {
      metaTags.push({ name: 'robots', content: 'noindex, nofollow' });
    }
    
    // Create and append all meta tags
    metaTags.forEach(({ name, property, content }) => {
      if (!content) return;
      
      const meta = document.createElement('meta');
      if (name) meta.setAttribute('name', name);
      if (property) meta.setAttribute('property', property);
      meta.setAttribute('content', content);
      document.head.appendChild(meta);
    });
    
    // Handle canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonical || window.location.href);
    
    // Add structured data if provided
    if (structuredData) {
      let scriptTag = document.querySelector('script[type="application/ld+json"]');
      if (scriptTag) {
        scriptTag.textContent = JSON.stringify(structuredData);
      } else {
        scriptTag = document.createElement('script');
        scriptTag.setAttribute('type', 'application/ld+json');
        scriptTag.textContent = JSON.stringify(structuredData);
        document.head.appendChild(scriptTag);
      }
    }
    
    // Cleanup function
    return () => {
      // We don't remove the tags on cleanup as they should persist between route changes
    };
  }, [title, description, canonical, ogImage, ogType, twitterCard, noIndex, structuredData]);
  
  // This component doesn't render anything visible
  return null;
};

export default SEO;
